import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notes } from '../services/api';
import { useNdf } from '../NdfContext';
import './Notes.css';
import { eur, km, dateFr } from '../services/format';
import Visionneuse from './Visionneuse';

const ETAPES = ['soumise', 'validee', 'transmise', 'remboursee'];
const LIBELLES = { soumise: 'Soumise', validee: 'Validée', transmise: 'Transmise compta', remboursee: 'Remboursée' };

// Détail d'une note : lignes + justificatifs + avancement dans le circuit.
// Composant PARTAGÉ : page /note/:id (compta, liens) ET volet droit de
// l'onglet Notes (maître-détail) — un seul format d'aperçu partout.
function DetailNote({ id, onFermer, onRappelee, integre = false }) {
  const navigate = useNavigate();
  const { user } = useNdf();
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState('');
  const [apercu, setApercu] = useState(null);
  const [rappelDemande, setRappelDemande] = useState(false);
  const [rappelEnCours, setRappelEnCours] = useState(false);

  useEffect(() => {
    setDonnees(null);
    setErreur('');
    setRappelDemande(false);
    setRappelEnCours(false);
    notes.lire(id)
      .then((res) => setDonnees(res.data))
      .catch((err) => setErreur(err.response?.data?.error || 'Note introuvable'));
  }, [id]);

  if (erreur && !donnees) return <div className="nf-erreur-bandeau">{erreur}</div>;
  if (!donnees) return null;
  const { note, depenses } = donnees;
  const estAuteur = user && note.user_email.toLowerCase() === user.email.toLowerCase();

  const rappeler = async () => {
    setErreur('');
    setRappelEnCours(true);
    try {
      await notes.rappeler(note.id);
      if (onRappelee) onRappelee(); else navigate('/');
    } catch (err) {
      setErreur(err.response?.data?.error || 'Rappel impossible');
      setRappelDemande(false);
      setRappelEnCours(false);
    }
  };
  const total = depenses.reduce((s, d) => s + (parseFloat(d.montant_ttc) || 0), 0);
  const etapeCourante = note.statut === 'refusee' ? -1 : ETAPES.indexOf(note.statut);

  return (
    <div>
      <button type="button" className={`fd-retour ${integre ? 'nt-retour-mobile' : ''}`}
        onClick={() => (onFermer ? onFermer() : navigate(-1))}>← Retour</button>
      <div className="nt-detail-entete">
        <h2 className="nf-titre-page">
          Note nº {note.id} — {note.prenom} {note.nom || note.user_email}
          {note.societe_nom ? ` · ${note.societe_nom}` : ''}
          {note.dossier_libelle ? ` · 📁 ${note.dossier_libelle}` : ''}
        </h2>
        <span className="nf-montant nt-detail-total">{eur(total)}</span>
      </div>

      {note.statut === 'refusee' ? (
        <div className="nf-erreur-bandeau">
          Note refusée par {note.validee_par}{note.motif_refus ? ` — ${note.motif_refus}` : ''}.
          Les dépenses sont revenues dans votre brouillon : corrigez-les puis soumettez une nouvelle note.
        </div>
      ) : (
        <ol className="nt-etapes">
          {ETAPES.map((e, i) => (
            <li key={e} className={i <= etapeCourante ? 'faite' : ''}>{LIBELLES[e]}</li>
          ))}
        </ol>
      )}

      {erreur && donnees && <div className="nf-erreur-bandeau">{erreur}</div>}

      {estAuteur && note.statut === 'soumise' && (
        <div className="nt-rappel">
          {!rappelDemande ? (
            <button type="button" className="nf-btn" onClick={() => setRappelDemande(true)}>
              ↩ Rappeler cette note pour la modifier
            </button>
          ) : (
            <>
              <span className="nt-rappel-question">Les dépenses reviendront dans votre brouillon.</span>
              <button type="button" className="nf-btn" onClick={() => setRappelDemande(false)}>Annuler</button>
              <button type="button" className="nf-btn nf-btn--principal" onClick={rappeler} disabled={rappelEnCours}>
                {rappelEnCours ? 'Rappel…' : 'Confirmer le rappel'}
              </button>
            </>
          )}
        </div>
      )}

      <ul className="nt-lignes">
        {depenses.map((d) => (
          <li key={d.id} className="nt-ligne-dep">
            <span className="nt-ligne-icone">{d.categorie_icone || '🧾'}</span>
            <span className="nt-corps">
              <span className="nt-titre">
                {d.type === 'km' ? `${km(d.distance_km)} — ${d.motif_deplacement || 'déplacement'}` : (d.marchand || 'Dépense')}
              </span>
              <span className="nt-sous">
                {d.date_depense ? dateFr(d.date_depense) : ''}
                {d.categorie_libelle ? ` · ${d.categorie_libelle}` : ''}
                {d.moyen_paiement === 'carte_societe' ? ' · carte société' : ''}
                {!note.dossier_id && d.dossier_libelle ? ` · 📁 ${d.dossier_libelle}` : ''}
              </span>
              {d.alertes && d.alertes.length > 0 && (
                <span className="nt-alertes">{d.alertes.map((a) => <span key={a} className="nf-alerte">{a}</span>)}</span>
              )}
            </span>
            <span className="nt-droite">
              <span className="nf-montant">{eur(d.montant_ttc)}</span>
              {d.justificatif_url && (
                <button type="button" className="nt-justif"
                  onClick={() => setApercu({ url: d.justificatif_url, type: d.justificatif_type, titre: d.marchand || 'Justificatif' })}>
                  justificatif
                </button>
              )}
            </span>
          </li>
        ))}
      </ul>
      {apercu && <Visionneuse {...apercu} onClose={() => setApercu(null)} />}
    </div>
  );
}

export default DetailNote;
